import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ApiPage from './components/ApiPage';
import GettingStarted from './components/GettingStarted';
import StatusCodes from './components/StatusCodes';
import WebhookPage from './components/WebhookPage';
import LandingPage from './components/LandingPage';
import { getProduct, productPath } from './data/products';
import { AppProvider } from './context/AppContext';

const MOBILE_BP = 1024;

function currentPath() {
  return (window.location.pathname.replace(/\/+$/, '') || '/');
}

function parsePath() {
  const raw = typeof window !== 'undefined' ? window.location.pathname : '/';
  const parts = raw.split('/').filter(Boolean);
  if (!parts.length) return { view: 'landing', productId: null, page: 'home' };
  const product = getProduct(parts[0]);
  if (!product) return { view: 'landing', productId: null, page: 'home' };
  if (product.externalUrl) return { view: 'landing', productId: null, page: 'home' };
  return { view: 'docs', productId: product.id, page: parts[1] || 'home' };
}

function writePath(productId, page) {
  const next = productPath(getProduct(productId), page);
  if (currentPath() === next) return;
  window.history.pushState(null, '', next);
}

function bootstrapLocation() {
  if (typeof window === 'undefined') return;
  try {
    const saved = sessionStorage.getItem('spa-redirect');
    if (saved) {
      sessionStorage.removeItem('spa-redirect');
      window.history.replaceState(null, '', saved);
    }
  } catch (_) { /* private mode */ }

  let path = currentPath();
  const hash = window.location.hash || '';
  const hashMatch = path === '/' ? hash.match(/^#\/?(.*)$/) : null;
  if (hashMatch && hashMatch[1] && hashMatch[1] !== 'products') {
    const parts = hashMatch[1].split('/').filter(Boolean);
    const product = parts.length ? getProduct(parts[0]) : null;
    if (product?.externalUrl) {
      window.location.replace(product.externalUrl);
      return;
    }
    window.history.replaceState(null, '', productPath(product, parts[1] || 'home'));
    return;
  }

  const parts = path.split('/').filter(Boolean);
  const product = parts.length ? getProduct(parts[0]) : null;
  if (product?.externalUrl) {
    window.location.replace(product.externalUrl);
    return;
  }
  if (!product) return;
  const canonical = productPath(product, parts[1] || 'home');
  if (path !== canonical) window.history.replaceState(null, '', canonical);
}

bootstrapLocation();

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BP : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

export default function App() {
  const isMobile = useIsMobile();
  const initial = parsePath();
  const [view, setView] = useState(initial.view);
  const [productId, setProductId] = useState(initial.productId);
  const [activePage, setActivePage] = useState(initial.page);
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth > MOBILE_BP : true
  );
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const product = getProduct(productId);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!(isMobile && sidebarOpen && view === 'docs')) {
      document.body.classList.remove('is-drawer-open');
      document.body.style.top = '';
      document.body.style.position = '';
      document.body.style.width = '';
      return undefined;
    }

    const scrollY = window.scrollY;
    document.body.classList.add('is-drawer-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.classList.remove('is-drawer-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [isMobile, sidebarOpen, view]);

  useEffect(() => {
    const onPop = () => {
      const next = parsePath();
      setView(next.view);
      setProductId(next.productId);
      setActivePage(next.page);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const openProduct = useCallback((id, page = 'home') => {
    const next = getProduct(id);
    if (next?.externalUrl) {
      window.open(next.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setProductId(id);
    setActivePage(page);
    setView('docs');
    writePath(id, page);
    window.scrollTo(0, 0);
    if (window.innerWidth <= MOBILE_BP) setSidebarOpen(false);
  }, []);

  const handleSetActivePage = useCallback((id) => {
    setActivePage(id);
    writePath(productId, id);
    if (window.innerWidth <= MOBILE_BP) setSidebarOpen(false);
  }, [productId]);

  const backToProducts = useCallback(() => {
    setView('landing');
    setProductId(null);
    setActivePage('home');
    writePath(null);
    window.scrollTo(0, 0);
  }, []);

  if (view !== 'docs' || !product) {
    return (
      <LandingPage
        theme={theme}
        toggleTheme={toggleTheme}
        onOpenProduct={openProduct}
      />
    );
  }

  const apis = product.apis;
  const currentApi = apis.find(a => a.id === activePage);

  const getPageTitle = () => {
    if (activePage === 'home') return 'Getting Started';
    if (activePage === 'status-codes') return 'Status Codes';
    if (activePage === 'webhook') return 'Webhook Reference';
    return currentApi?.name || 'Documentation';
  };

  const renderContent = () => {
    if (activePage === 'home') return <GettingStarted product={product} setActivePage={handleSetActivePage} />;
    if (activePage === 'status-codes') return <StatusCodes statusCodes={product.statusCodes} />;
    if (activePage === 'webhook') return <WebhookPage webhook={product.webhook} />;
    if (currentApi) return <ApiPage key={`${product.id}-${activePage}`} api={currentApi} />;
    return <GettingStarted product={product} setActivePage={handleSetActivePage} />;
  };

  return (
    <AppProvider key={product.id} product={product}>
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)' }}>
      <Header
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        theme={theme}
        toggleTheme={toggleTheme}
        activePage={activePage}
        setActivePage={handleSetActivePage}
        product={product}
        apis={apis}
        onBackToProducts={backToProducts}
      />

      <div style={{ display: 'flex', marginTop: 'var(--header-h)' }}>
        {sidebarOpen && isMobile && (
          <button
            type="button"
            className="sidebar-backdrop is-visible"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          apis={apis}
          activePage={activePage}
          setActivePage={handleSetActivePage}
          open={sidebarOpen}
        />

        <main className={`main-content${sidebarOpen && !isMobile ? ' is-pushed' : ''}`}>
          <div style={{
            height: 2,
            background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 25%, #ec4899 50%, #f97316 75%, #22c55e 100%)',
          }} />

          <div className="breadcrumb-bar">
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" style={{ opacity: 0.5 }}>
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h4a1 1 0 001-1v-3h2v3a1 1 0 001 1h4a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
              {product.headerLabel}
            </span>
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" style={{ opacity: 0.3, flexShrink: 0 }}>
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getPageTitle()}</span>
            {currentApi && (
              <span style={{
                marginLeft: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                background: 'var(--post-bg)', color: 'var(--post)',
                border: '1px solid var(--post-border)',
                fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em',
                flexShrink: 0,
              }}>POST</span>
            )}
          </div>

          {renderContent()}
        </main>
      </div>
    </div>
    </AppProvider>
  );
}
